import { SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL, type FlappyPair } from './vocabulary.js';

const HIGH_SCORE_ID = 'car-racer';

export type CarRacerProps = {
  onExit: (result: { score: number; caught: number; missed: number; xp: number; distance: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

// --- Mobile-first portrait world ---------------------------------------
const WORLD_W = 480;
const WORLD_H = 760;
const ROAD_LEFT = 60;
const ROAD_RIGHT = 420;
const ROAD_W = ROAD_RIGHT - ROAD_LEFT;
const GROUND_COLOR = 'rgb(84, 130, 54)';  // roadside grass
const ROAD_COLOR = 'rgb(46, 52, 60)';
const ROAD_EDGE = 'rgb(250, 250, 250)';
const LANE_MARK = 'rgb(255, 220, 80)';

// Lanes: 3 strict lane centers. Word cards use LEFT + RIGHT only; obstacles
// and pickups can occupy any of the three.
const LANE_LEFT_X = ROAD_LEFT + ROAD_W * 0.22;
const LANE_CENTER_X = ROAD_LEFT + ROAD_W * 0.5;
const LANE_RIGHT_X = ROAD_LEFT + ROAD_W * 0.78;
const LANES_X: readonly number[] = [LANE_LEFT_X, LANE_CENTER_X, LANE_RIGHT_X];

const CAR_W = 50;
const CAR_H = 90;
const CAR_Y = WORLD_H - 120;

// Physics: critically-damped spring toward pointer, speed-dependent clamp.
const STEER_STIFFNESS = 220;            // spring k
const STEER_DAMPING = 2 * Math.sqrt(STEER_STIFFNESS); // critical damping
const STEER_VX_CAP_BASE = 520;          // max lateral vx at low speed
const STEER_VX_CAP_TOP_MUL = 0.55;      // at MAX_SPEED, cap = base * this

const BASE_SPEED = 300;
const MAX_SPEED = 520;
const SPEED_TAU = 8;                    // seconds to ~63% of max (ease-out)

const CARD_W = 160;
const CARD_H = 66;
const CHALLENGE_INTERVAL = 2.6;
const XP_CORRECT = 18;
const HIT_GRACE_MS = 1000;
const HEART_INTERVAL_MIN = 9;           // seconds between heart pickups
const HEART_INTERVAL_MAX = 14;
const HEART_R = 18;

type CardState = 'neutral' | 'correct' | 'wrong';

type WordCard = {
  id: string;
  challengeId: string;
  label: string;
  isCorrect: boolean;
  x: number;
  y: number;
  consumed: boolean;
  state: CardState;
};

type ObstacleVariant = 'barrel' | 'oil' | 'cone';

type Obstacle = {
  id: string;
  variant: ObstacleVariant;
  x: number;
  y: number;
  rot: number;
  consumed: boolean;
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
};

type Heart = {
  id: string;
  x: number;
  y: number;
  bob: number; // phase offset for hover animation
  consumed: boolean;
};

type Divider = {
  id: string;
  challengeId: string;
  x: number;
  y: number;
  consumed: boolean;
};

const DIVIDER_W = 22;
const DIVIDER_H = 86; // slightly taller than the word cards so it can't be skirted

type ScorchMark = { x: number; y: number; r: number };
type TireTrack = { x: number; y: number; life: number };

// Preserves theme difficulty curve across runs.
function blockShuffle<T>(arr: T[], blockSize = 10): T[] {
  const out: T[] = [];
  for (let i = 0; i < arr.length; i += blockSize) {
    const block = arr.slice(i, i + blockSize);
    for (let j = block.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      const tmp = block[j] as T;
      block[j] = block[k] as T;
      block[k] = tmp;
    }
    out.push(...block);
  }
  return out;
}

/**
 * Slingu Racer — top-down endless racer. O jogador guia um carro azul por
 * uma pista enquanto cartões de palavras em inglês descem em pares (correto
 * verde + distrator vermelho). Atravessar o correto = XP. Atravessar o
 * errado = o carro explode: partículas voam, tela treme, perde 1 coração e
 * o carro respawna. 3 explosões = fim.
 *
 * Controle mobile: arrastar o dedo horizontalmente na tela.
 * Desktop: setas ou A/D.
 */
export function CarRacer({ onExit }: CarRacerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [phase, setPhase] = useState<Phase>('menu');
  const [hud, setHud] = useState({ score: 0, caught: 0, missed: 0, hearts: 3, distance: 0 });
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const promptRef = useRef<string>('Arraste o dedo pra dirigir');

  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const pairsRef = useRef<FlappyPair[]>(LEGACY_POOL);
  pairsRef.current = LEGACY_POOL;

  const gameRef = useRef({
    scrollY: 0,
    speed: BASE_SPEED,
    speedT: 0,              // elapsed time for ease-out accel curve
    carX: WORLD_W / 2,
    carVX: 0,               // lateral velocity (spring physics)
    carTargetX: WORLD_W / 2,
    carTilt: 0,
    wheelSpin: 0,
    cards: [] as WordCard[],
    obstacles: [] as Obstacle[],
    dividers: [] as Divider[],
    hearts_pickups: [] as Heart[],
    queue: [] as FlappyPair[],
    spawnTimer: 1.0,
    obstacleTimer: 1.6,
    obstacleCounter: 0,
    heartTimer: HEART_INTERVAL_MIN + Math.random() * (HEART_INTERVAL_MAX - HEART_INTERVAL_MIN),
    heartCounter: 0,
    particles: [] as Particle[],
    scorches: [] as ScorchMark[],
    tireTracks: [] as TireTrack[],
    slipT: 0,
    score: 0,
    caught: 0,
    missed: 0,
    hearts: 3,
    alive: true,
    hitCooldown: 0,
    challengeCounter: 0,
    distance: 0,
    trauma: 0,              // 0..1 — trauma model for shake
    freezeT: 0,             // hit-stop freeze timer
    flashT: 0,              // white flash overlay
    exploded: false,
    respawnTimer: 0,
    lastCorrectSides: [] as boolean[],
  });

  const startedRef = useRef(false);

  useEffect(() => {
    if (phase !== 'playing') return;
    if (startedRef.current) return;
    startedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const g = gameRef.current;
    // Reset game state
    g.scrollY = 0;
    g.speed = BASE_SPEED;
    g.speedT = 0;
    g.carX = WORLD_W / 2;
    g.carVX = 0;
    g.carTargetX = WORLD_W / 2;
    g.carTilt = 0;
    g.wheelSpin = 0;
    g.cards = [];
    g.obstacles = [];
    g.dividers = [];
    g.hearts_pickups = [];
    g.queue = blockShuffle(pairsRef.current, 10);
    g.spawnTimer = 1.0;
    g.obstacleTimer = 2.2;
    g.obstacleCounter = 0;
    g.heartTimer = HEART_INTERVAL_MIN + Math.random() * (HEART_INTERVAL_MAX - HEART_INTERVAL_MIN);
    g.heartCounter = 0;
    g.particles = [];
    g.scorches = [];
    g.tireTracks = [];
    g.slipT = 0;
    g.trauma = 0;
    g.freezeT = 0;
    g.flashT = 0;
    g.lastCorrectSides = [];
    g.score = 0;
    g.caught = 0;
    g.missed = 0;
    g.hearts = 3;
    g.alive = true;
    g.hitCooldown = 0;
    g.challengeCounter = 0;
    g.distance = 0;
    g.exploded = false;
    g.respawnTimer = 0;
    setHud({ score: 0, caught: 0, missed: 0, hearts: 3, distance: 0 });
    promptRef.current = 'Arraste o dedo pra dirigir';

    let disposed = false;

    function resize() {
      if (!canvas) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const parent = canvas.parentElement;
      const parentW = parent?.clientWidth ?? window.innerWidth;
      const parentH = parent?.clientHeight ?? window.innerHeight;
      const scale = Math.min(parentW / WORLD_W, parentH / WORLD_H);
      canvas.style.width = `${WORLD_W * scale}px`;
      canvas.style.height = `${WORLD_H * scale}px`;
      canvas.width = Math.floor(WORLD_W * dpr);
      canvas.height = Math.floor(WORLD_H * dpr);
    }
    resize();
    window.addEventListener('resize', resize);

    // --- Input: track pointer X within the canvas to steer the car ------
    let isPointerDown = false;
    function canvasXFromClient(clientX: number): number {
      const rect = canvas!.getBoundingClientRect();
      const normalized = (clientX - rect.left) / rect.width;
      return Math.max(0, Math.min(1, normalized)) * WORLD_W;
    }
    function onPointerDown(e: PointerEvent) {
      e.preventDefault();
      isPointerDown = true;
      if (!g.exploded) {
        g.carTargetX = clampCarX(canvasXFromClient(e.clientX));
      }
    }
    function onPointerMove(e: PointerEvent) {
      if (!isPointerDown) return;
      e.preventDefault();
      if (!g.exploded) {
        g.carTargetX = clampCarX(canvasXFromClient(e.clientX));
      }
    }
    function onPointerUp() {
      isPointerDown = false;
    }
    function onKey(e: KeyboardEvent) {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        e.preventDefault();
        g.carTargetX = clampCarX(g.carTargetX - 60);
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        e.preventDefault();
        g.carTargetX = clampCarX(g.carTargetX + 60);
      }
    }
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('keydown', onKey);

    // --- Main loop ------------------------------------------------------
    let lastT = performance.now();
    function frame(nowMs: number) {
      if (disposed) return;
      const dt = Math.min(0.05, (nowMs - lastT) / 1000);
      lastT = nowMs;
      update(dt);
      render();
      rafRef.current = requestAnimationFrame(frame);
    }

    function update(dt: number) {
      if (!g.alive) return;

      // Respawn if exploded
      if (g.exploded) {
        g.respawnTimer -= dt;
        if (g.respawnTimer <= 0) {
          if (g.hearts <= 0) {
            endGame();
            return;
          }
          g.exploded = false;
          g.carX = WORLD_W / 2;
          g.carTargetX = WORLD_W / 2;
          g.speed = BASE_SPEED;
          g.hitCooldown = HIT_GRACE_MS;
          // Clear off-screen hazards so we don't immediately explode again
          g.cards = g.cards.filter((c) => c.y < CAR_Y - 200);
        }
      }

      // Decay freeze-frame and flash regardless of explosion state
      if (g.freezeT > 0) g.freezeT -= dt;
      if (g.flashT > 0) g.flashT -= dt;

      // Speed ramp — ease-out curve: speed = BASE + (MAX - BASE) * (1 - e^(-t/tau))
      if (!g.exploded && g.freezeT <= 0) {
        g.speedT += dt;
        const target = BASE_SPEED + (MAX_SPEED - BASE_SPEED) * (1 - Math.exp(-g.speedT / SPEED_TAU));
        // Slip/cone already reduced speed — recover toward target gently.
        g.speed = g.speed + (target - g.speed) * Math.min(1, dt * 1.2);
      }

      // Car steering — critically-damped spring toward target, with
      // speed-dependent lateral velocity cap. Makes it tight at low speed,
      // floaty (but predictable) at high speed.
      if (!g.exploded && g.freezeT <= 0) {
        const speedRatio = Math.max(0, Math.min(1, (g.speed - BASE_SPEED) / (MAX_SPEED - BASE_SPEED)));
        const stiffness = STEER_STIFFNESS * (1 - 0.3 * speedRatio);
        // Oil softens the spring significantly — slippery feel.
        const slipMul = g.slipT > 0 ? 0.35 : 1;
        const dx = g.carTargetX - g.carX;
        const ax = stiffness * slipMul * dx - STEER_DAMPING * g.carVX;
        g.carVX += ax * dt;
        // Cap lateral velocity (gets stricter at higher speed)
        const vxCap = STEER_VX_CAP_BASE * (1 - (1 - STEER_VX_CAP_TOP_MUL) * speedRatio) * slipMul;
        g.carVX = Math.max(-vxCap, Math.min(vxCap, g.carVX));
        // Oil wiggle
        if (g.slipT > 0) {
          g.carVX += Math.sin(performance.now() * 0.018) * 180 * dt;
          g.slipT -= dt;
        }
        g.carX += g.carVX * dt;
        // Hit a wall? kill lateral velocity
        const beforeClamp = g.carX;
        g.carX = clampCarX(g.carX);
        if (g.carX !== beforeClamp) g.carVX *= -0.2;
        // Tilt = visual only, follows vx
        const tiltTarget = Math.max(-0.3, Math.min(0.3, g.carVX / 420));
        g.carTilt += (tiltTarget - g.carTilt) * Math.min(1, dt * 10);
        if (g.slipT > 0) g.carTilt += Math.sin(performance.now() * 0.012) * 0.15;
      } else if (g.exploded) {
        g.carTilt *= 0.9;
        g.carVX *= 0.85;
      }

      // Tire skid marks when steering hard OR slipping
      if (!g.exploded && g.freezeT <= 0) {
        const hardSteer = Math.abs(g.carVX) > 180 || g.slipT > 0;
        const rate = hardSteer ? 25 : 6;
        if (Math.random() < rate * dt) {
          g.tireTracks.push({
            x: g.carX - CAR_W / 2 + 2,
            y: CAR_Y + CAR_H / 2 - 6,
            life: hardSteer ? 2.2 : 1.2,
          });
          g.tireTracks.push({
            x: g.carX + CAR_W / 2 - 4,
            y: CAR_Y + CAR_H / 2 - 6,
            life: hardSteer ? 2.2 : 1.2,
          });
        }
      }

      // Scroll world down (road moves toward player)
      if (!g.exploded && g.freezeT <= 0) {
        g.scrollY += g.speed * dt;
        g.distance += g.speed * dt * 0.04;
        g.score += dt * 4;
        g.wheelSpin += g.speed * dt * 0.02;
      }

      if (g.hitCooldown > 0) g.hitCooldown -= dt * 1000;
      // Trauma decays linearly; shake intensity uses trauma^2
      if (g.trauma > 0) g.trauma = Math.max(0, g.trauma - dt * 1.2);

      // Particles
      g.particles = g.particles.filter((p) => p.life > 0);
      for (const p of g.particles) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 80 * dt; // slight "gravity" in top-down = particles settle
        p.vx *= 0.96;
        p.life -= dt;
      }

      // Spawn cadence
      if (!g.exploded) {
        g.spawnTimer -= dt;
        if (g.spawnTimer <= 0) {
          if (g.queue.length === 0) {
            if (g.cards.length === 0 && g.obstacles.length === 0) endGame();
          } else {
            spawnChallenge(g.queue.shift()!);
            g.spawnTimer = CHALLENGE_INTERVAL;
          }
        }
        // Obstacle cadence — scales slightly with current speed ratio.
        g.obstacleTimer -= dt;
        if (g.obstacleTimer <= 0) {
          spawnObstacle();
          const speedRatio = Math.max(0, Math.min(1, (g.speed - BASE_SPEED) / (MAX_SPEED - BASE_SPEED)));
          const baseMin = 1.5 - 0.35 * speedRatio;
          g.obstacleTimer = baseMin + Math.random() * 0.9;
        }
        // Heart pickup cadence — rare and on a clear lane.
        g.heartTimer -= dt;
        if (g.heartTimer <= 0) {
          spawnHeart();
          g.heartTimer = HEART_INTERVAL_MIN + Math.random() * (HEART_INTERVAL_MAX - HEART_INTERVAL_MIN);
        }
      }

      // Move cards + check collision
      for (const card of g.cards) {
        card.y += g.speed * dt;
        if (card.consumed) continue;
        // AABB collision with car
        const cLeft = card.x - CARD_W / 2;
        const cRight = card.x + CARD_W / 2;
        const cTop = card.y - CARD_H / 2;
        const cBot = card.y + CARD_H / 2;
        const carLeft = g.carX - CAR_W / 2 + 4;
        const carRight = g.carX + CAR_W / 2 - 4;
        const carTop = CAR_Y - CAR_H / 2 + 6;
        const carBot = CAR_Y + CAR_H / 2 - 6;
        if (!g.exploded && g.hitCooldown <= 0 &&
            carRight > cLeft && carLeft < cRight &&
            carBot > cTop && carTop < cBot) {
          onCardHit(card);
          continue;
        }
        // Missed: passed below player without hit
        if (card.y - CARD_H / 2 > WORLD_H + 20) {
          card.consumed = true;
        }
      }
      // Keep cards with feedback on screen until they exit; neutral consumed
      // (untouched sibling / missed pair) stop rendering but stay in array
      // until fully off-screen.
      g.cards = g.cards.filter((c) => c.y - CARD_H / 2 < WORLD_H + 60);

      // Move obstacles + check collision
      const OBS_R = 22;
      for (const obs of g.obstacles) {
        obs.y += g.speed * dt;
        if (obs.consumed) continue;
        const carLeft = g.carX - CAR_W / 2 + 4;
        const carRight = g.carX + CAR_W / 2 - 4;
        const carTop = CAR_Y - CAR_H / 2 + 6;
        const carBot = CAR_Y + CAR_H / 2 - 6;
        if (!g.exploded && g.hitCooldown <= 0 &&
            carRight > obs.x - OBS_R && carLeft < obs.x + OBS_R &&
            carBot > obs.y - OBS_R && carTop < obs.y + OBS_R) {
          onObstacleHit(obs);
        }
      }
      g.obstacles = g.obstacles.filter((o) => o.y - OBS_R < WORLD_H + 60);

      // Move dividers + physically block the car (no penalty, just wall)
      for (const d of g.dividers) {
        d.y += g.speed * dt;
        if (d.consumed) continue;
        if (g.exploded) continue;
        const carLeft = g.carX - CAR_W / 2 + 4;
        const carRight = g.carX + CAR_W / 2 - 4;
        const carTop = CAR_Y - CAR_H / 2 + 6;
        const carBot = CAR_Y + CAR_H / 2 - 6;
        const dLeft = d.x - DIVIDER_W / 2;
        const dRight = d.x + DIVIDER_W / 2;
        const dTop = d.y - DIVIDER_H / 2;
        const dBot = d.y + DIVIDER_H / 2;
        if (carRight > dLeft && carLeft < dRight && carBot > dTop && carTop < dBot) {
          // Push the car out to whichever side it's closer to
          if (g.carX < d.x) {
            g.carX = dLeft - (CAR_W / 2 - 4);
          } else {
            g.carX = dRight + (CAR_W / 2 - 4);
          }
          // Kill lateral momentum (soft bounce) and add small trauma
          g.carVX *= -0.25;
          g.trauma = Math.min(0.35, g.trauma + 0.08);
          // Throttled bump sfx so it doesn't spam
          if (g.hitCooldown <= 0) {
            sfxRef.current.play('tap');
            g.hitCooldown = 160;
          }
        }
      }
      g.dividers = g.dividers.filter((d) => d.y - DIVIDER_H / 2 < WORLD_H + 60);

      // Move heart pickups + check collision
      for (const h of g.hearts_pickups) {
        h.y += g.speed * dt;
        h.bob += dt * 4;
        if (h.consumed) continue;
        const carLeft = g.carX - CAR_W / 2 + 4;
        const carRight = g.carX + CAR_W / 2 - 4;
        const carTop = CAR_Y - CAR_H / 2 + 6;
        const carBot = CAR_Y + CAR_H / 2 - 6;
        if (!g.exploded &&
            carRight > h.x - HEART_R && carLeft < h.x + HEART_R &&
            carBot > h.y - HEART_R && carTop < h.y + HEART_R) {
          onHeartPickup(h);
        }
      }
      g.hearts_pickups = g.hearts_pickups.filter((h) => !h.consumed && h.y - HEART_R < WORLD_H + 60);

      // Scorches fade
      g.scorches = g.scorches.filter((s) => s.y < WORLD_H + 40);
      for (const s of g.scorches) s.y += g.speed * dt;

      // Tire tracks scroll with the road and decay
      g.tireTracks = g.tireTracks.filter((t) => t.life > 0 && t.y < WORLD_H + 20);
      for (const t of g.tireTracks) {
        t.y += g.speed * dt;
        t.life -= dt;
      }

      // Publish HUD
      setHud((prev) => {
        const score = Math.floor(g.score);
        const distance = Math.floor(g.distance);
        if (
          prev.score === score &&
          prev.distance === distance &&
          prev.caught === g.caught &&
          prev.missed === g.missed &&
          prev.hearts === g.hearts
        ) {
          return prev;
        }
        return { score, distance, caught: g.caught, missed: g.missed, hearts: g.hearts };
      });
    }

    function spawnChallenge(pair: FlappyPair) {
      g.challengeCounter += 1;
      const id = `c${g.challengeCounter}`;
      // Random side, but break a >=3 streak on the same side.
      let correctLeft = Math.random() < 0.5;
      const last = g.lastCorrectSides;
      if (last.length >= 2 && last[0] === last[1] && last[0] === correctLeft) {
        correctLeft = !correctLeft;
      }
      g.lastCorrectSides.push(correctLeft);
      if (g.lastCorrectSides.length > 2) g.lastCorrectSides.shift();
      g.cards.push({
        id: id + 'L',
        challengeId: id,
        label: correctLeft ? pair.correct : pair.wrong,
        isCorrect: correctLeft,
        x: LANE_LEFT_X,
        y: -CARD_H / 2 - 20,
        consumed: false,
        state: 'neutral',
      });
      g.cards.push({
        id: id + 'R',
        challengeId: id,
        label: correctLeft ? pair.wrong : pair.correct,
        isCorrect: !correctLeft,
        x: LANE_RIGHT_X,
        y: -CARD_H / 2 - 20,
        consumed: false,
        state: 'neutral',
      });
      // Central divider — prevents threading down the middle and hitting both
      // cards simultaneously. Forces the player to commit to a lane.
      g.dividers.push({
        id: id + 'D',
        challengeId: id,
        x: LANE_CENTER_X,
        y: -DIVIDER_H / 2 - 20,
        consumed: false,
      });
      promptRef.current = `Passe sobre: "${pair.promptPt ?? pair.correct}"`;
    }

    function spawnObstacle() {
      // Snap to one of the 3 lane centers (tiny ±4px jitter for visual life)
      // and use a lookahead: never occupy the lane where the CORRECT card will
      // arrive within the next ~2s, so the player always has a clean path.
      const spawnY = -60;
      const lookAhead = 280;

      // Lanes currently clear of obstacles within the lookahead band
      const freeLanes = LANES_X.filter((lx) => {
        return !g.obstacles.some(
          (o) => !o.consumed && Math.abs(o.x - lx) < 46 && o.y > spawnY - lookAhead && o.y < spawnY + lookAhead,
        );
      });
      if (freeLanes.length === 0) return;

      // Lanes where the CORRECT card is heading — protect those
      const correctLanes = new Set<number>();
      for (const c of g.cards) {
        if (c.consumed || !c.isCorrect) continue;
        if (c.y > spawnY - lookAhead && c.y < spawnY + lookAhead) {
          correctLanes.add(c.x);
        }
      }
      let candidates = freeLanes.filter((lx) => !correctLanes.has(lx));
      if (candidates.length === 0) candidates = freeLanes; // fallback: still space out

      const lane = candidates[Math.floor(Math.random() * candidates.length)]!;
      const x = lane + (Math.random() - 0.5) * 8;

      g.obstacleCounter += 1;
      const roll = Math.random();
      const variant: ObstacleVariant =
        roll < 0.18 ? 'barrel' : roll < 0.58 ? 'oil' : 'cone';

      g.obstacles.push({
        id: `o${g.obstacleCounter}`,
        variant,
        x,
        y: spawnY,
        rot: Math.random() * Math.PI,
        consumed: false,
      });
    }

    function onCardHit(card: WordCard) {
      card.consumed = true;
      card.state = card.isCorrect ? 'correct' : 'wrong';
      // Hide the untouched sibling — we only reveal the card the car ran over.
      for (const sib of g.cards) {
        if (sib.challengeId === card.challengeId && sib.id !== card.id) {
          sib.consumed = true;
        }
      }
      if (card.isCorrect) {
        g.score += XP_CORRECT;
        g.caught += 1;
        sfxRef.current.play('xp-gain');
        spawnSparkles(card.x, card.y, 'rgb(255, 230, 120)', 10);
      } else {
        explode(card.x, card.y);
      }
    }

    function onObstacleHit(obs: Obstacle) {
      obs.consumed = true;
      if (obs.variant === 'barrel') {
        explode(obs.x, obs.y);
        return;
      }
      if (obs.variant === 'oil') {
        g.speed = Math.max(BASE_SPEED * 0.55, g.speed * 0.55);
        g.slipT = 0.9;
        g.trauma = Math.min(1, g.trauma + 0.35);
        g.hitCooldown = 350;
        sfxRef.current.play('hit');
        spawnSmoke(obs.x, obs.y, 14);
        return;
      }
      // cone — minor bump
      g.speed = Math.max(BASE_SPEED * 0.85, g.speed * 0.8);
      g.trauma = Math.min(1, g.trauma + 0.22);
      g.hitCooldown = 250;
      sfxRef.current.play('tap');
      spawnSparkles(obs.x, obs.y, 'rgb(255, 150, 60)', 8);
    }

    function onHeartPickup(h: Heart) {
      h.consumed = true;
      if (g.hearts < 3) {
        g.hearts += 1;
      } else {
        g.score += 30; // overflow bonus
      }
      sfxRef.current.play('xp-gain');
      // Pink sparkle burst
      spawnSparkles(h.x, h.y, 'rgb(246, 140, 180)', 14);
      spawnSparkles(h.x, h.y, 'rgb(255, 220, 230)', 6);
    }

    function spawnHeart() {
      // Place on a safe lane that doesn't overlap any obstacle or card within
      // a forward look-ahead window. Prefer lanes currently clear of hazards.
      const spawnY = -HEART_R - 20;
      const lookAhead = 260;
      const lanes = LANES_X.filter((lx) => {
        return !g.obstacles.some(
          (o) => !o.consumed && Math.abs(o.x - lx) < 46 && o.y > spawnY - lookAhead && o.y < spawnY + lookAhead,
        ) && !g.cards.some(
          (c) => !c.consumed && Math.abs(c.x - lx) < CARD_W / 2 + 8 && c.y > spawnY - lookAhead && c.y < spawnY + lookAhead,
        );
      });
      if (lanes.length === 0) return;
      const lane = lanes[Math.floor(Math.random() * lanes.length)]!;
      g.heartCounter += 1;
      g.hearts_pickups.push({
        id: `h${g.heartCounter}`,
        x: lane,
        y: spawnY,
        bob: Math.random() * Math.PI * 2,
        consumed: false,
      });
    }

    function spawnSmoke(x: number, y: number, count: number) {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 40 + Math.random() * 80;
        g.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed * 0.6,
          life: 0.7 + Math.random() * 0.5,
          maxLife: 1.2,
          size: 6 + Math.random() * 5,
          color: `rgba(70, 70, 78, ${0.55 + Math.random() * 0.25})`,
        });
      }
    }

    function explode(cx: number, cy: number) {
      g.exploded = true;
      g.respawnTimer = 1.3;
      g.missed += 1;
      g.hearts -= 1;
      g.trauma = 1;
      g.freezeT = 0.1;   // brief hit-stop
      g.flashT = 0.18;   // white flash
      sfxRef.current.play('hit');
      sfxRef.current.play('heart-loss');
      sfxRef.current.play('game-over');
      // Scorch mark where car was
      g.scorches.push({ x: g.carX, y: CAR_Y, r: CAR_W * 0.7 });
      // Big fiery burst
      const palette = ['rgb(255, 220, 60)', 'rgb(255, 150, 40)', 'rgb(240, 70, 40)', 'rgb(40, 40, 40)'];
      for (let i = 0; i < 42; i++) {
        const angle = (i / 42) * Math.PI * 2 + Math.random() * 0.3;
        const speed = 140 + Math.random() * 320;
        g.particles.push({
          x: g.carX + (Math.random() - 0.5) * 10,
          y: CAR_Y + (Math.random() - 0.5) * 10,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed * 0.7,
          life: 0.6 + Math.random() * 0.5,
          maxLife: 1.1,
          size: 4 + Math.random() * 6,
          color: palette[i % palette.length]!,
        });
      }
      // Shockwave ring
      for (let i = 0; i < 18; i++) {
        const angle = (i / 18) * Math.PI * 2;
        g.particles.push({
          x: g.carX,
          y: CAR_Y,
          vx: Math.cos(angle) * 260,
          vy: Math.sin(angle) * 260,
          life: 0.45,
          maxLife: 0.45,
          size: 3,
          color: 'rgba(255, 255, 255, 0.85)',
        });
      }
      void cx;
      void cy;
    }

    function spawnSparkles(x: number, y: number, color: string, count: number) {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 80 + Math.random() * 180;
        g.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0.5 + Math.random() * 0.3,
          maxLife: 0.8,
          size: 3 + Math.random() * 3,
          color,
        });
      }
    }

    function endGame() {
      if (!g.alive) return;
      g.alive = false;
      const finalScore = Math.floor(g.score);
      const result = commitScore(HIGH_SCORE_ID, finalScore);
      setHighScore(result.best);
      setNewRecord(result.isNew);
      setPhase('over');
    }

    // --- Render ----------------------------------------------------------
    function render() {
      const ctx = canvas!.getContext('2d');
      if (!ctx) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      // Trauma-based shake: offset ∝ trauma^2 (Cooper shake). Max ~11px.
      const shakeMag = 11 * g.trauma * g.trauma;
      const shakeX = (Math.random() - 0.5) * 2 * shakeMag;
      const shakeY = (Math.random() - 0.5) * 2 * shakeMag;
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr);

      // Grass background
      ctx.fillStyle = GROUND_COLOR;
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
      // Roadside stripes (parallax trees)
      drawRoadside(ctx, g.scrollY);

      // Road
      ctx.fillStyle = ROAD_COLOR;
      ctx.fillRect(ROAD_LEFT, 0, ROAD_W, WORLD_H);

      // Road shoulder stripes (white solid edges)
      ctx.fillStyle = ROAD_EDGE;
      ctx.fillRect(ROAD_LEFT - 2, 0, 4, WORLD_H);
      ctx.fillRect(ROAD_RIGHT - 2, 0, 4, WORLD_H);

      // Center dashed lane line (2 lanes)
      const centerX = (ROAD_LEFT + ROAD_RIGHT) / 2;
      ctx.fillStyle = LANE_MARK;
      const dashH = 44;
      const dashGap = 30;
      const unit = dashH + dashGap;
      const offset = g.scrollY % unit;
      for (let y = -unit + offset; y < WORLD_H + unit; y += unit) {
        ctx.fillRect(centerX - 4, y, 8, dashH);
      }

      // Tire tracks — drawn BEFORE scorches/cards so scorches layer on top
      for (const t of g.tireTracks) {
        const a = Math.max(0, Math.min(1, t.life / 1.6)) * 0.35;
        ctx.fillStyle = `rgba(10, 10, 14, ${a})`;
        ctx.fillRect(t.x, t.y, 5, 10);
      }

      // Scorch marks
      for (const s of g.scorches) {
        const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
        grad.addColorStop(0, 'rgba(10, 10, 10, 0.85)');
        grad.addColorStop(0.7, 'rgba(10, 10, 10, 0.4)');
        grad.addColorStop(1, 'rgba(10, 10, 10, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Obstacles (below cards so cards stay readable)
      for (const obs of g.obstacles) {
        if (obs.consumed) continue;
        drawObstacle(ctx, obs);
      }

      // Heart pickups
      for (const h of g.hearts_pickups) {
        if (h.consumed) continue;
        drawHeartPickup(ctx, h);
      }

      // Word cards — hide only consumed-with-no-feedback; cards the car ran
      // over keep rendering in their result color until they exit the screen.
      for (const card of g.cards) {
        if (card.consumed && card.state === 'neutral') continue;
        drawCard(ctx, card.x, card.y, card.label, card.state);
      }

      // Challenge dividers — rendered AFTER cards so the fence sits on top of
      // the card shadow and clearly marks the lane split.
      for (const d of g.dividers) {
        if (d.consumed) continue;
        drawDivider(ctx, d.x, d.y);
      }

      // Speed lines on the grass when going fast — suggests motion blur
      if (g.speed > BASE_SPEED + 40 && !g.exploded) {
        const intensity = Math.min(1, (g.speed - BASE_SPEED) / (MAX_SPEED - BASE_SPEED));
        ctx.fillStyle = `rgba(255, 255, 255, ${0.15 * intensity})`;
        const lineCount = Math.floor(6 * intensity);
        for (let i = 0; i < lineCount; i++) {
          const sx = i < lineCount / 2 ? Math.random() * (ROAD_LEFT - 20) + 4 : ROAD_RIGHT + 20 + Math.random() * (WORLD_W - ROAD_RIGHT - 24);
          const sy = (g.scrollY * 1.4 + i * 137) % WORLD_H;
          ctx.fillRect(sx, sy, 3, 24);
        }
      }

      // Car (or wreckage if exploded)
      if (g.exploded) {
        drawWreck(ctx, g.carX, CAR_Y, g.respawnTimer);
      } else {
        const blink = g.hitCooldown > 0 && Math.floor(g.hitCooldown / 80) % 2 === 0;
        if (!blink) drawCar(ctx, g.carX, CAR_Y, g.carTilt, g.wheelSpin);
      }

      // Particles
      for (const p of g.particles) {
        const a = Math.max(0, Math.min(1, p.life / p.maxLife));
        ctx.save();
        ctx.globalAlpha = a;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Prompt banner — hugs the top between the HUD (left) and SFX toggle
      // (right). Compact so it doesn't crowd the play field mid-screen.
      const promptText = promptRef.current;
      if (promptText) {
        ctx.save();
        const bh = 38;
        const bw = 260;
        const by = 10;
        const bx = WORLD_W / 2 - bw / 2;
        ctx.fillStyle = 'rgba(255,255,255,0.94)';
        roundRect(ctx, bx, by, bw, bh, 19);
        ctx.fill();
        ctx.strokeStyle = 'rgba(20, 83, 163, 0.2)';
        ctx.lineWidth = 1.5;
        roundRect(ctx, bx, by, bw, bh, 19);
        ctx.stroke();
        ctx.fillStyle = 'rgb(20, 83, 163)';
        ctx.font = 'bold 17px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(promptText, WORLD_W / 2, by + bh / 2 + 1);
        ctx.restore();
      }

      // White flash overlay (from explosions) — readable "hit" impact.
      if (g.flashT > 0) {
        const a = Math.min(1, g.flashT / 0.18) * 0.55;
        ctx.save();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = `rgba(255, 245, 220, ${a})`;
        ctx.fillRect(0, 0, WORLD_W, WORLD_H);
        ctx.restore();
      }
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', resize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedRef.current = false;
    };
  }, [phase]);

  return (
    <div className="sl-minigame sl-minigame--portrait" ref={containerRef}>
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExitRef.current({ score: 0, caught: 0, missed: 0, xp: 0, distance: 0 })}
        aria-label="Fechar jogo"
      >
        ×
      </button>

      <canvas ref={canvasRef} className="sl-minigame__canvas" />

      <div className="sl-flappy__hud">
        <span className="sl-quizrun__hearts">
          {'♥'.repeat(hud.hearts)}
          {'·'.repeat(Math.max(0, 3 - hud.hearts))}
        </span>
        <span className="sl-quizrun__score">{hud.score} XP</span>
        <span className="sl-quizrun__distance">{hud.distance} m</span>
      </div>
      <div className="sl-flappy__sfx">
        <SfxToggle variant="icon" />
      </div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2>
            <span aria-hidden>🏎️</span> Slingu Racer
          </h2>
          <p>
            Arraste o dedo (ou use ←/→) pra dirigir. Dois cards{' '}
            <strong style={{ color: 'rgb(28, 176, 246)' }}>azuis</strong> descem
            pela pista — passe por cima da tradução certa e o card vira{' '}
            <strong style={{ color: 'rgb(88, 204, 2)' }}>verde</strong>; passou
            na errada, vira <strong style={{ color: 'rgb(238, 85, 85)' }}>vermelho</strong>{' '}
            e o carro explode. Cuidado também com barris ⚠️, poça de óleo e cones
            espalhados pela pista. 3 explosões = fim. Pool de 1000+ palavras.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => {
                sfx.play('start');
                setNewRecord(false);
                setPhase('playing');
              }}
            >
              Acelerar
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : hud.caught > hud.missed * 2 ? 'Bom piloto!' : 'Explodiu geral.'}</h2>
          <p>
            XP: <strong>{hud.score}</strong> · {hud.distance} m
            <br />
            Acertos: {hud.caught} · Explosões: {hud.missed}
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--ghost"
              onClick={() =>
                onExitRef.current({
                  score: hud.score,
                  caught: hud.caught,
                  missed: hud.missed,
                  xp: hud.score,
                  distance: hud.distance,
                })
              }
            >
              Voltar pro app
            </button>
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => {
                sfx.play('start');
                setHud({ score: 0, caught: 0, missed: 0, hearts: 3, distance: 0 });
                setPhase('playing');
              }}
            >
              Correr de novo
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// --- Helpers ------------------------------------------------------------

function clampCarX(x: number): number {
  const min = ROAD_LEFT + CAR_W / 2 + 6;
  const max = ROAD_RIGHT - CAR_W / 2 - 6;
  return Math.max(min, Math.min(max, x));
}

function drawCar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  tilt: number,
  wheelSpin: number,
) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tilt * 0.35);

  // Shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.beginPath();
  ctx.ellipse(2, 6, CAR_W / 2 - 4, CAR_H / 2 - 8, 0, 0, Math.PI * 2);
  ctx.fill();

  // Tires (front + rear, each side)
  ctx.fillStyle = 'rgb(22, 22, 22)';
  const tireW = 8;
  const tireH = 16;
  for (const [dx, dy] of [[-CAR_W / 2 - 1, -CAR_H / 2 + 14], [CAR_W / 2 - tireW + 1, -CAR_H / 2 + 14], [-CAR_W / 2 - 1, CAR_H / 2 - 30], [CAR_W / 2 - tireW + 1, CAR_H / 2 - 30]] as const) {
    ctx.fillRect(dx, dy, tireW, tireH);
    // Hub spin indicator
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = 'rgba(120, 120, 120, 0.8)';
    ctx.fillRect(dx + 2, dy + ((wheelSpin * 20) % tireH + tireH) % tireH, tireW - 4, 2);
    ctx.restore();
  }

  // Body base (dark outline)
  ctx.fillStyle = 'rgb(14, 66, 130)';
  roundRect(ctx, -CAR_W / 2, -CAR_H / 2, CAR_W, CAR_H, 10);
  ctx.fill();

  // Body color (blue — Slingu-themed)
  ctx.fillStyle = 'rgb(28, 176, 246)';
  roundRect(ctx, -CAR_W / 2 + 3, -CAR_H / 2 + 3, CAR_W - 6, CAR_H - 6, 8);
  ctx.fill();

  // Roof highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  roundRect(ctx, -CAR_W / 2 + 8, -CAR_H / 2 + 8, 10, CAR_H - 16, 4);
  ctx.fill();

  // Windshield (back)
  ctx.fillStyle = 'rgb(35, 48, 70)';
  roundRect(ctx, -CAR_W / 2 + 8, -CAR_H / 2 + 12, CAR_W - 16, 20, 3);
  ctx.fill();
  // Windshield (front)
  roundRect(ctx, -CAR_W / 2 + 8, CAR_H / 2 - 32, CAR_W - 16, 22, 3);
  ctx.fill();

  // Hood "Slingu eyes" — little pink dots for personality
  ctx.fillStyle = 'rgba(255, 134, 208, 0.85)';
  ctx.beginPath();
  ctx.arc(-8, CAR_H / 2 - 8, 3, 0, Math.PI * 2);
  ctx.arc(8, CAR_H / 2 - 8, 3, 0, Math.PI * 2);
  ctx.fill();

  // Front headlights
  ctx.fillStyle = 'rgb(255, 248, 200)';
  roundRect(ctx, -CAR_W / 2 + 6, -CAR_H / 2 + 2, 10, 4, 2);
  ctx.fill();
  roundRect(ctx, CAR_W / 2 - 16, -CAR_H / 2 + 2, 10, 4, 2);
  ctx.fill();

  // Rear lights
  ctx.fillStyle = 'rgb(200, 30, 30)';
  roundRect(ctx, -CAR_W / 2 + 6, CAR_H / 2 - 6, 10, 4, 2);
  ctx.fill();
  roundRect(ctx, CAR_W / 2 - 16, CAR_H / 2 - 6, 10, 4, 2);
  ctx.fill();

  // Antenna sparkle (Slingu flair)
  ctx.strokeStyle = 'rgb(20, 83, 163)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, -CAR_H / 2);
  ctx.lineTo(0, -CAR_H / 2 - 10);
  ctx.stroke();
  ctx.fillStyle = 'rgb(255, 232, 150)';
  ctx.beginPath();
  ctx.arc(0, -CAR_H / 2 - 12, 3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawWreck(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  respawn: number,
) {
  const fade = Math.max(0, Math.min(1, respawn / 1.3));
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((1 - fade) * 0.4);
  ctx.globalAlpha = 0.6 + 0.4 * fade;
  // Charred body
  ctx.fillStyle = 'rgb(35, 20, 10)';
  roundRect(ctx, -CAR_W / 2, -CAR_H / 2, CAR_W, CAR_H, 10);
  ctx.fill();
  // Smoke patches
  ctx.fillStyle = 'rgba(70, 70, 70, 0.6)';
  for (let i = 0; i < 4; i++) {
    const dx = (Math.random() - 0.5) * CAR_W;
    const dy = (Math.random() - 0.5) * CAR_H;
    ctx.beginPath();
    ctx.arc(dx, dy, 10 + Math.random() * 6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  text: string,
  state: CardState,
) {
  const bg =
    state === 'correct'
      ? 'rgb(88, 204, 2)'
      : state === 'wrong'
        ? 'rgb(238, 85, 85)'
        : 'rgb(28, 176, 246)';
  const shade =
    state === 'correct'
      ? 'rgb(58, 136, 18)'
      : state === 'wrong'
        ? 'rgb(180, 40, 40)'
        : 'rgb(20, 124, 186)';
  const w = CARD_W;
  const h = CARD_H;
  ctx.save();
  // Drop shadow
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = shade;
  roundRect(ctx, cx - w / 2 + 2, cy - h / 2 + 5, w, h, 18);
  ctx.fill();
  ctx.globalAlpha = 1;
  // Body
  ctx.fillStyle = bg;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 18);
  ctx.fill();
  // Highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  roundRect(ctx, cx - w / 2 + 4, cy - h / 2 + 4, w - 8, 14, 14);
  ctx.fill();
  // Outline
  ctx.strokeStyle = shade;
  ctx.lineWidth = 3;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 18);
  ctx.stroke();
  // Text
  ctx.fillStyle = 'rgb(255, 255, 255)';
  ctx.font = 'bold 24px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 2);
  ctx.restore();
}

function drawDivider(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const w = DIVIDER_W;
  const h = DIVIDER_H;
  ctx.save();
  // Ground shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  roundRect(ctx, cx - w / 2 + 2, cy - h / 2 + 5, w, h, 6);
  ctx.fill();
  // Base (dark)
  ctx.fillStyle = 'rgb(28, 28, 32)';
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 6);
  ctx.fill();
  // Yellow/black hazard stripes
  ctx.save();
  ctx.beginPath();
  roundRect(ctx, cx - w / 2 + 2, cy - h / 2 + 2, w - 4, h - 4, 4);
  ctx.clip();
  ctx.fillStyle = 'rgb(248, 204, 40)';
  const stripeH = 10;
  for (let y = cy - h / 2 - stripeH; y < cy + h / 2 + stripeH; y += stripeH * 2) {
    ctx.save();
    ctx.translate(cx, y);
    ctx.rotate(-0.6);
    ctx.fillRect(-w, 0, w * 2, stripeH);
    ctx.restore();
  }
  ctx.restore();
  // Top and bottom caps (rounded posts)
  ctx.fillStyle = 'rgb(200, 40, 40)';
  ctx.beginPath();
  ctx.arc(cx, cy - h / 2 + 2, w / 2 + 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(240, 80, 80)';
  ctx.beginPath();
  ctx.arc(cx - 2, cy - h / 2, w / 2 - 2, 0, Math.PI * 2);
  ctx.fill();
  // Outline
  ctx.strokeStyle = 'rgba(15, 15, 18, 0.9)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 6);
  ctx.stroke();
  ctx.restore();
}

function drawHeartPickup(ctx: CanvasRenderingContext2D, h: Heart) {
  const bob = Math.sin(h.bob) * 3;
  const pulse = 0.85 + Math.sin(h.bob * 1.6) * 0.15;
  ctx.save();
  ctx.translate(h.x, h.y + bob);

  // Outer glow — distinguishes pickup from obstacle
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, HEART_R * 2);
  glow.addColorStop(0, 'rgba(255, 180, 210, 0.6)');
  glow.addColorStop(0.6, 'rgba(255, 180, 210, 0.18)');
  glow.addColorStop(1, 'rgba(255, 180, 210, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, HEART_R * 2, 0, Math.PI * 2);
  ctx.fill();

  // Ground shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.beginPath();
  ctx.ellipse(2, HEART_R + 4, HEART_R * 0.8, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  // Heart shape via two circles + triangle
  ctx.scale(pulse, pulse);
  ctx.fillStyle = 'rgb(180, 30, 60)';
  ctx.beginPath();
  ctx.arc(-HEART_R / 2 + 1, -HEART_R / 4, HEART_R / 2, 0, Math.PI * 2);
  ctx.arc(HEART_R / 2 - 1, -HEART_R / 4, HEART_R / 2, 0, Math.PI * 2);
  ctx.moveTo(-HEART_R, -HEART_R / 5);
  ctx.lineTo(0, HEART_R);
  ctx.lineTo(HEART_R, -HEART_R / 5);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgb(240, 80, 110)';
  ctx.beginPath();
  ctx.arc(-HEART_R / 2 + 2, -HEART_R / 4 + 1, HEART_R / 2 - 2, 0, Math.PI * 2);
  ctx.arc(HEART_R / 2 - 2, -HEART_R / 4 + 1, HEART_R / 2 - 2, 0, Math.PI * 2);
  ctx.moveTo(-HEART_R + 2, -HEART_R / 5 + 1);
  ctx.lineTo(0, HEART_R - 2);
  ctx.lineTo(HEART_R - 2, -HEART_R / 5 + 1);
  ctx.closePath();
  ctx.fill();

  // Sheen
  ctx.fillStyle = 'rgba(255, 220, 230, 0.7)';
  ctx.beginPath();
  ctx.ellipse(-HEART_R / 3, -HEART_R / 3, 4, 2.5, -0.4, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawObstacle(ctx: CanvasRenderingContext2D, obs: Obstacle) {
  ctx.save();
  ctx.translate(obs.x, obs.y);
  if (obs.variant === 'barrel') {
    // Ground shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.beginPath();
    ctx.ellipse(3, 8, 22, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    // Body: red + yellow hazard stripes (top-down view)
    ctx.fillStyle = 'rgb(180, 40, 40)';
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgb(250, 210, 60)';
    ctx.fillRect(-22, -6, 44, 4);
    ctx.fillRect(-22, 2, 44, 4);
    // Rim highlight
    ctx.strokeStyle = 'rgb(220, 70, 70)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, Math.PI * 2);
    ctx.stroke();
    // Warning symbol
    ctx.fillStyle = 'rgb(20, 20, 20)';
    ctx.font = 'bold 16px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚠', 0, 1);
  } else if (obs.variant === 'oil') {
    // Oil slick — irregular dark blob
    ctx.rotate(obs.rot);
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 30);
    grad.addColorStop(0, 'rgba(8, 8, 14, 0.9)');
    grad.addColorStop(0.8, 'rgba(8, 8, 14, 0.6)');
    grad.addColorStop(1, 'rgba(8, 8, 14, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(0, 0, 32, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    // Rainbow sheen
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = 'rgba(100, 140, 220, 0.45)';
    ctx.beginPath();
    ctx.ellipse(-6, -4, 14, 8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(220, 100, 220, 0.35)';
    ctx.beginPath();
    ctx.ellipse(8, 2, 10, 6, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Cone — orange traffic cone (top-down: triangle with ring)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(2, 6, 18, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgb(240, 110, 30)';
    ctx.beginPath();
    ctx.arc(0, 0, 18, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgb(255, 170, 90)';
    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.beginPath();
    ctx.arc(0, 0, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawRoadside(ctx: CanvasRenderingContext2D, scrollY: number) {
  const spacing = 110;
  const offset = scrollY % spacing;
  // Deterministic per-slot variety using a hash so the scene doesn't flicker.
  const hash = (n: number) => {
    const x = Math.sin(n * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };

  const drawSide = (side: 'L' | 'R') => {
    const baseX = side === 'L' ? ROAD_LEFT / 2 : ROAD_RIGHT + (WORLD_W - ROAD_RIGHT) / 2;
    const sideSeed = side === 'L' ? 0 : 1000;
    for (let y = -spacing + offset; y < WORLD_H + spacing; y += spacing) {
      const slot = Math.floor((scrollY + y) / spacing) + sideSeed;
      const h1 = hash(slot);
      const h2 = hash(slot + 0.37);
      const h3 = hash(slot + 0.71);

      // Main prop per slot
      const main = h1;
      const jitterX = (h2 - 0.5) * 30;
      const px = baseX + jitterX;
      const py = y;
      if (main < 0.45) {
        drawTreeRound(ctx, px, py, 0.9 + h3 * 0.4);
      } else if (main < 0.7) {
        drawTreeConifer(ctx, px, py, 0.9 + h3 * 0.4);
      } else if (main < 0.82) {
        drawRock(ctx, px, py, 8 + h3 * 6);
      } else if (main < 0.92) {
        drawFlowerPatch(ctx, px, py, h3);
      } else {
        drawSign(ctx, px, py, h3);
      }

      // Secondary small prop at half offset
      const slot2 = slot + 0.5;
      const s1 = hash(slot2);
      const s2 = hash(slot2 + 0.19);
      const sx = baseX + (s2 - 0.5) * 46;
      const sy = y + spacing / 2;
      if (s1 < 0.5) {
        drawFlowerPatch(ctx, sx, sy, hash(slot2 + 0.5));
      } else if (s1 < 0.8) {
        drawRock(ctx, sx, sy, 4 + hash(slot2 + 0.8) * 4);
      } else {
        drawTreeRound(ctx, sx, sy, 0.6 + hash(slot2 + 0.9) * 0.3);
      }
    }
  };
  drawSide('L');
  drawSide('R');

  // Road edge with soft gravel shoulder
  ctx.fillStyle = 'rgba(120, 108, 96, 0.55)';
  ctx.fillRect(ROAD_LEFT - 10, 0, 8, WORLD_H);
  ctx.fillRect(ROAD_RIGHT + 2, 0, 8, WORLD_H);

  // Metal guard rails
  ctx.strokeStyle = 'rgba(220, 220, 225, 0.75)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(ROAD_LEFT - 12, 0);
  ctx.lineTo(ROAD_LEFT - 12, WORLD_H);
  ctx.moveTo(ROAD_RIGHT + 12, 0);
  ctx.lineTo(ROAD_RIGHT + 12, WORLD_H);
  ctx.stroke();
  // Rail posts (scroll with world)
  const postGap = 50;
  const postOff = scrollY % postGap;
  ctx.fillStyle = 'rgb(90, 90, 96)';
  for (let y = -postGap + postOff; y < WORLD_H + postGap; y += postGap) {
    ctx.fillRect(ROAD_LEFT - 15, y, 4, 10);
    ctx.fillRect(ROAD_RIGHT + 11, y, 4, 10);
  }
}

function drawTreeRound(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  // Shadow
  ctx.fillStyle = 'rgba(20, 40, 20, 0.3)';
  ctx.beginPath();
  ctx.ellipse(4, 18, 18, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  // Trunk
  ctx.fillStyle = 'rgb(90, 60, 36)';
  ctx.fillRect(-3, 0, 6, 14);
  // Foliage layers
  ctx.fillStyle = 'rgb(42, 96, 36)';
  ctx.beginPath();
  ctx.arc(0, -4, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(62, 124, 48)';
  ctx.beginPath();
  ctx.arc(-4, -8, 11, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(92, 156, 72)';
  ctx.beginPath();
  ctx.arc(-6, -12, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawTreeConifer(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.fillStyle = 'rgba(20, 40, 20, 0.3)';
  ctx.beginPath();
  ctx.ellipse(3, 18, 14, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(80, 52, 30)';
  ctx.fillRect(-2, 4, 4, 14);
  // Stacked triangles (pine shape)
  ctx.fillStyle = 'rgb(30, 86, 44)';
  ctx.beginPath();
  ctx.moveTo(0, -22); ctx.lineTo(-12, 0); ctx.lineTo(12, 0); ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgb(50, 116, 60)';
  ctx.beginPath();
  ctx.moveTo(0, -14); ctx.lineTo(-10, 4); ctx.lineTo(10, 4); ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgb(74, 146, 82)';
  ctx.beginPath();
  ctx.moveTo(0, -6); ctx.lineTo(-8, 8); ctx.lineTo(8, 8); ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawRock(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = 'rgba(30, 30, 30, 0.35)';
  ctx.beginPath();
  ctx.ellipse(2, r * 0.7, r * 1.1, r * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(120, 118, 112)';
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgb(170, 168, 160)';
  ctx.beginPath();
  ctx.ellipse(-r * 0.3, -r * 0.3, r * 0.45, r * 0.25, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawFlowerPatch(ctx: CanvasRenderingContext2D, x: number, y: number, seed: number) {
  const palette = ['rgb(246, 140, 180)', 'rgb(255, 210, 90)', 'rgb(240, 90, 90)', 'rgb(180, 130, 240)'];
  const color = palette[Math.floor(seed * palette.length) % palette.length]!;
  ctx.save();
  ctx.translate(x, y);
  for (let i = 0; i < 5; i++) {
    const dx = (Math.sin(seed * 10 + i * 1.37) * 0.5) * 14;
    const dy = (Math.cos(seed * 7 + i * 2.1) * 0.5) * 14;
    // Leaves
    ctx.fillStyle = 'rgb(50, 116, 60)';
    ctx.beginPath();
    ctx.arc(dx - 2, dy + 2, 2.2, 0, Math.PI * 2);
    ctx.fill();
    // Bloom
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(dx, dy, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgb(255, 245, 180)';
    ctx.beginPath();
    ctx.arc(dx, dy, 1, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawSign(ctx: CanvasRenderingContext2D, x: number, y: number, seed: number) {
  const emojis = ['🏁', '⚠️', '🌵', '🔆', '📣'];
  const emoji = emojis[Math.floor(seed * emojis.length) % emojis.length]!;
  ctx.save();
  ctx.translate(x, y);
  // Post
  ctx.fillStyle = 'rgb(80, 62, 40)';
  ctx.fillRect(-1.5, 0, 3, 16);
  // Board
  ctx.fillStyle = 'rgb(244, 238, 216)';
  roundRect(ctx, -14, -14, 28, 20, 4);
  ctx.fill();
  ctx.strokeStyle = 'rgb(120, 90, 40)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, -14, -14, 28, 20, 4);
  ctx.stroke();
  ctx.font = '16px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 0, -3);
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
